import { useState } from 'react'
import { cn } from '@/lib/utils'
import { Copy, CheckCircle2 } from 'lucide-react'

/**
 * Copyable code. Shared by the agent docs and the integration-test reports — both exist
 * to be pasted somewhere else, so the copy affordance is the point rather than decoration.
 */

export function CopyButton({
  text,
  label,
  className,
}: {
  text: string
  label?: string
  className?: string
}) {
  const [copied, setCopied] = useState(false)

  async function copy() {
    await navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <button
      type="button"
      onClick={() => void copy()}
      aria-label={`คัดลอก${label ? ` ${label}` : ''}`}
      className={cn(
        'flex items-center gap-1 font-mono text-[10px] text-muted-foreground hover:text-foreground transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm px-1.5 py-0.5',
        className,
      )}
    >
      {copied ? <CheckCircle2 className="h-3 w-3 text-success" /> : <Copy className="h-3 w-3" />}
      {copied ? 'คัดลอกแล้ว' : label ?? 'คัดลอก'}
    </button>
  )
}

export function CodeBlock({
  code,
  lang = 'bash',
  className,
}: {
  code: string
  lang?: string
  className?: string
}) {
  return (
    <div className={cn('relative rounded-[var(--radius-sm)] border border-border bg-muted overflow-hidden', className)}>
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-border bg-secondary/60">
        <span className="font-mono text-[10px] text-muted-foreground">{lang}</span>
        <CopyButton text={code} />
      </div>
      <pre className="p-4 overflow-x-auto text-xs font-mono text-foreground leading-relaxed">
        <code>{code}</code>
      </pre>
    </div>
  )
}
